import type { ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { actionProps } from "@/lib/controlAnalytics";
import { ROUTES } from "@/lib/routes";
import { useOptionalSession } from "@/auth/session";
import AreaHere from "@/components/layout/AreaHere";
import ReadyNotifications from "@/components/layout/ReadyNotifications";
import { GateProvider } from "./Gate";
import IntentOnReturn from "./IntentOnReturn";
import { ResumeFirstProvider } from "./ResumeFirst";
import SiteFooter from "./SiteFooter";
import SiteHeader from "./SiteHeader";
import TabBar from "./TabBar";

/** Places whose page is one card of a fixed size, so the footer can show under it. */
const FITS_ONE_SCREEN: ReadonlySet<string> = new Set([ROUTES.SUPPORT]);

/**
 * The frame every place sits in, for everyone: the header, the page, the
 * footer, and on a phone the tab bar. The page is the view transition's
 * moving part (`site-page`); the header and the tab bar hold still while
 * their indicators slide (index.css).
 *
 * The frame is `relative` and its own stacking context, so the tab bar
 * stays beneath the sheets, which are portalled to the body.
 */
export default function SiteLayout({ children }: { children: ReactNode }) {
    const session = useOptionalSession();
    const { pathname } = useLocation();
    return (
        <GateProvider>
            <ResumeFirstProvider>
                <div className="relative isolate flex min-h-dvh flex-col bg-bg text-fg">
                    {session && (
                        <>
                            <ReadyNotifications key={session.userId} userId={session.userId} />
                            <AreaHere userId={session.userId} />
                            <IntentOnReturn />
                        </>
                    )}
                    <a
                        {...actionProps("navigation_clicked", {
                            source: "SiteLayout",
                            target: "#main",
                        })}
                        href="#main"
                        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-3 focus:z-40 focus:rounded-control focus:bg-card focus:px-3 focus:py-2"
                    >
                        Skip to the page
                    </a>
                    <SiteHeader />
                    {/* A screen tall under the header at the least: the footer starts
                        below the fold, so a page that fills in never pushes it down
                        in view (a layout shift Lighthouse counts). A place that fits
                        one screen fills between the header and the footer instead. */}
                    <main
                        id="main"
                        className={`site-page mx-auto w-full max-w-[1240px] flex-1 px-8 pb-16 pt-7 max-[760px]:px-4 max-[760px]:pb-24 max-[760px]:pt-5 ${FITS_ONE_SCREEN.has(pathname) ? "flex flex-col" : "min-h-[calc(100dvh-4rem)]"}`}
                    >
                        {children}
                    </main>
                    <SiteFooter />
                    <TabBar />
                </div>
            </ResumeFirstProvider>
        </GateProvider>
    );
}
